import React, { useEffect, useState, useMemo } from "react";
import Select from "react-select";
import { toast } from "react-toastify";
import BarreNavigation from "../../components/BarreNavigation/BarreNavigation";
import Navbarhorizontal from "../../components/Navbarhorizontal/Navbarhorizontal";
import Modal from "../../components/Modal/Modal";
import API_BASE_URL from "../../API/ConfigAPI";
import FacturationEditTable from "../../components/FacturationEditTable/FacturationEditTable";
import FacturationForm from "../../components/FacturationForm/FacturationForm";
import MonthSelector from "../../components/MonthSelector/MonthSelector";
import StatutBadges from "../../components/StatutBadges/StatutBadges";
import {
  handleInputChange,
  handleNumericInput,
  handleReductionChange,
  handleRegularisationChange,
  handleAvoirHTChange,
  handleCommentaireChange,
  prepareFacturationData,
} from "../../utils/formUtils";
import { monthToDate } from "../../utils/dateUtils";
import useFacturation from "../../hooks/useFacturation";
import SpinnerLoader from "../../components/SpinnerLoader/SpinnerLoader";
import "./facturation.css";

function Facturation() {
  const {
    facturations,
    selectedMonth,
    setSelectedMonth,
    activeTabs,
    isLoading,
    setFacturations,
  } = useFacturation();

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentFacturation, setCurrentFacturation] = useState({});
  const [formError, setFormError] = useState("");
  const [searchRaisonSociale, setSearchRaisonSociale] = useState("");
  const [selectedStatut, setSelectedStatut] = useState(null);
  const [selectedGestionnaires, setSelectedGestionnaires] = useState([]);
  const [availableGestionnaires, setAvailableGestionnaires] = useState([]);

  const gestionnaireOptions = useMemo(
    () => availableGestionnaires.map((nom) => ({ label: nom, value: nom })),
    [availableGestionnaires]
  );

  useEffect(() => {
    const uniqueGestionnaireNoms = [
      ...new Set(facturations.map((f) => f.gestionnaire_nom)),
    ];
    setAvailableGestionnaires(uniqueGestionnaireNoms);
  }, [facturations]);

  useEffect(() => {
    const uniqueMonthsArray = Array.from(
      new Set(facturations.map((f) => f.mois_facturation))
    ).sort((a, b) => monthToDate(a) - monthToDate(b));

    if (uniqueMonthsArray.length > 0) {
      setSelectedMonth(uniqueMonthsArray[uniqueMonthsArray.length - 1]);
    }
  }, [facturations, setSelectedMonth]);

  const handleRaisonSocialeChange = (e) =>
    setSearchRaisonSociale(e.target.value);

  const handleSelectChange = (selectedOptions) => {
    const noms = selectedOptions?.map((option) => option.value) || [];
    setSelectedGestionnaires(noms);
  };

  const openEditModal = (facturation) => {
    setCurrentFacturation(facturation);
    setIsEditModalOpen(true);
  };

  const closeModal = () => {
    setIsEditModalOpen(false);
    setFormError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");
    const preparedData = prepareFacturationData(currentFacturation);

    try {
      const response = await fetch(
        `${API_BASE_URL}/home/api/facturations/${currentFacturation.id}/`,
        {
          method: "PUT",
          headers: {
            Authorization: `Token ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(preparedData),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Erreur lors de la mise à jour.");
      }
      setFacturations((prevFacturations) =>
        prevFacturations.map((facture) =>
          facture.id === currentFacturation.id
            ? { ...facture, ...preparedData }
            : facture
        )
      );
      setIsEditModalOpen(false);
      toast.success("La facturation a été mise à jour avec succès !");
    } catch (error) {
      console.error("Erreur:", error.message);
      setFormError("Une erreur s'est produite.");
      toast.error(error.message || "Erreur lors de la mise à jour.");
    }
  };

  const filterBySelectedMonth = (facturation) => {
    const facturationDate = monthToDate(facturation.mois_facturation);
    const selectedDate = monthToDate(selectedMonth);
    return (
      facturationDate.getMonth() === selectedDate.getMonth() &&
      facturationDate.getFullYear() === selectedDate.getFullYear()
    );
  };

  const facturationsBySelectedMonth = facturations.filter(
    filterBySelectedMonth
  );

  const totalParStatutMoisSelectionne = facturationsBySelectedMonth.reduce(
    (acc, { statut }) => {
      acc[statut] = (acc[statut] || 0) + 1;
      return acc;
    },
    {}
  );

  const displayTitleMonth = () => {
    const [month, year] = selectedMonth.split("-");
    return `${month.charAt(0).toUpperCase() + month.slice(1)} ${year}`;
  };

  return (
    <div className="parent-layout">
      <BarreNavigation />
      <div className="content">
        <Navbarhorizontal />
        <div className="container">
          <h1>Facturation de {displayTitleMonth()}</h1>

          <div className="search-filtre-container-fact">
            <input
              type="text"
              placeholder="Recherche"
              value={searchRaisonSociale}
              onChange={handleRaisonSocialeChange}
              className="search-input-facturation"
            />

            <Select
              className="filtres-gestionnaires"
              options={gestionnaireOptions}
              isMulti
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              onChange={handleSelectChange}
              allowSelectAll={true}
              value={gestionnaireOptions.filter((option) =>
                selectedGestionnaires.includes(option.value)
              )}
              placeholder="Filtre"
            />
          </div>

          <div className="select_month_status">
            <MonthSelector
              activeTabs={activeTabs}
              selectedMonth={selectedMonth}
              setSelectedMonth={setSelectedMonth}
            />
            <StatutBadges
              selectedStatut={selectedStatut}
              setSelectedStatut={setSelectedStatut}
              statusCounts={totalParStatutMoisSelectionne}
            />
          </div>

          <div className="table-container">
            {isLoading ? (
              <SpinnerLoader />
            ) : (
              <FacturationEditTable
                facturations={facturations}
                selectedGestionnaires={selectedGestionnaires}
                searchRaisonSociale={searchRaisonSociale}
                selectedStatut={selectedStatut}
                filterBySelectedMonth={filterBySelectedMonth}
                handleStatusChange={handleInputChange}
                openEditModal={openEditModal}
              />
            )}
          </div>
        </div>
      </div>

      <Modal
        isOpen={isEditModalOpen}
        onClose={closeModal}
        title="Déclarer les prestations"
      >
        <FacturationForm
          currentFacturation={currentFacturation}
          formError={formError}
          handleInputChange={(e) => handleInputChange(e, setCurrentFacturation)}
          handleNumericInput={(e) =>
            handleNumericInput(e, setCurrentFacturation)
          }
          handleReductionChange={(e) =>
            handleReductionChange(e, setCurrentFacturation)
          }
          handleRegularisationChange={(e) =>
            handleRegularisationChange(e, setCurrentFacturation)
          }
          handleAvoirHTChange={(e) =>
            handleAvoirHTChange(e, setCurrentFacturation)
          }
          handleCommentaireChange={(e) =>
            handleCommentaireChange(e, setCurrentFacturation)
          }
          handleSubmit={handleSubmit}
          closeModal={closeModal}
        />
      </Modal>
    </div>
  );
}

export default Facturation;
